import styled from '@emotion/styled'
import React from 'react'
import { normalizedWarrantName } from '@finance-lab/utils/shared'

import { CardContent, Card as MuiCard, Typography } from '@mui/material'
import { Box, spacing } from '@mui/system'
import { useTheme } from '@mui/material/styles'
import { DataGridPro, gridClasses } from '@mui/x-data-grid-pro'
import { enUS, svSE } from '@mui/x-data-grid-pro/locales'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { warrantGridCurrentPageAtom, warrantSearchTextfieldAtom } from '../../atoms/warrantAtom'
import { formatDateYYYYMMDD, formatInteger, formatMoney } from '../../packages/utils/numberFormatters'
import { calculateTotalAmount, calculateTotalAmountAndFormat } from '../../pages/warrants/WarrantOutcome'
import { WarrantName } from './WarrantName'
import { sumSubscriptionPeriodsSharesSubscribed, WarrantAmount } from './WarrantAmount'
import { calculateWarrantStatus, WarrantStatusDescription } from './WarrantStatus'
import { WarrantSubEndDate } from './WarrantSubEndDate'
import { WarrantSubPrice } from './WarrantSubPrice'
import { WarrantOutcomeChart } from './WarrantOutcomeChart'
import { CustomDataGridToolbar } from '../custom-grid-components/CustomDataGridToolbar'
import { createNoteIndicatorColumn, noteIndicatorGridSx } from './NoteRowIndicator'
import { useEntityNoteIds } from '../../hooks/useEntityNoteIds'

const Card = styled(MuiCard)(spacing)

function NoWarrants() {
    return (
        <Card mb={6}>
            <CardContent>
                <Typography variant='h6' gutterBottom>
                    Inga teckningsoptioner hittades
                </Typography>
            </CardContent>
        </Card>
    )
}

const gridTextI18n = (locale) => {
    if (locale?.startsWith('sv')) {
        return svSE.components.MuiDataGrid.defaultProps.localeText
    } else {
        return enUS.components.MuiDataGrid.defaultProps.localeText
    }
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga teckningsoptioner hittades</Typography>
        </Box>
    )
}

function WarrantsGrid({
    warrants,
    detailsPath,
    fields,
    sortModel = null,
    pagination = true,
    pageSizeInitialState = 10,
    pageSizeOptions = [10, 25, 50, 100],
}) {
    const { i18n } = useTranslation()
    const locale = i18n.language

    const theme = useTheme()
    const { entityIdsWithNotes, reloadEntityNoteIds } = useEntityNoteIds()

    detailsPath = detailsPath || ((c) => `/warrants/${c.id}`)

    const [warrantGridCurrentPage, setWarrantGridCurrentPage] = useAtom(warrantGridCurrentPageAtom)
    const [searchTextfield, setSearchTextfield] = useAtom(warrantSearchTextfieldAtom)

    const [pageSize, setPageSize] = React.useState(pageSizeInitialState)

    // Initialize filterModel with quickFilterValues and an empty items array
    const [filterModel, setFilterModel] = React.useState({
        items: [], // Ensure items is an empty array initially
        quickFilterValues: [searchTextfield], // Default quick filter value
    })

    const handleFilterModelChange = (newFilterModel) => {
        setFilterModel({
            ...newFilterModel,
            items: newFilterModel.items || [], // Ensure items is always an array
        })
        setWarrantGridCurrentPage(0)
        setSearchTextfield(newFilterModel.quickFilterValues[0])
    }

    const cols = [
        {
            field: 'name',
            headerName: 'Namn',
            flex: 1,
            valueGetter: (_, row) => normalizedWarrantName(row),
            renderCell: (params) => (
                <Link to={detailsPath(params.row)}>
                    <WarrantName warrant={params.row} />
                </Link>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            display: 'flex',
            valueGetter: (_, row) => {
                const status = calculateWarrantStatus(row)
                const hideUntil =
                    row.statusHideUntil && new Date(row.statusHideUntil) > new Date() ? row.statusHideUntil : null
                if (hideUntil) {
                    return `8-${formatDateYYYYMMDD(hideUntil)}-${status.stage}`
                }
                return `${status.status === 'complete' ? 9 : 0}-${status.stage}`
            },
            renderCell: (params) => <WarrantStatusDescription warrant={params.row} shortForm />,
        },
        {
            field: 'subStartDate',
            headerName: 'Första teckningsdag',
            flex: 1,
            valueFormatter: (value) => formatDateYYYYMMDD(value),
        },
        {
            field: 'subEndDate',
            headerName: 'Sista teckningsdag',
            flex: 1,
            valueGetter: (_, row) => {
                return row.subEndDate
            },
            sortComparator: (v1, v2) => {
                const date1 = new Date(v1)
                const date2 = new Date(v2)
                return date1 - date2
            },
            renderCell: (params) => <WarrantSubEndDate warrant={params.row} />,
        },
        {
            field: 'warrantSubPrice',
            headerName: 'Teckningskurs',
            flex: 1.5,
            sortable: false,
            renderCell: (params) => <WarrantSubPrice warrant={params.row} />,
        },
        {
            field: 'closePrice',
            headerName: 'Aktuell aktiekurs',
            flex: 1,
            valueFormatter: (value) => formatMoney(value, { locale, minDecimals: 2, maxDecimals: 5 }),
        },
        {
            field: 'warrantAmount',
            headerName: 'Belopp',
            flex: 1,
            sortable: false,
            renderCell: (params) => <WarrantAmount warrant={params.row} locale={locale} />,
        },
        {
            field: 'countSharesSubscribed',
            headerName: 'Antal tecknade optioner',
            flex: 1,
            renderCell: (params) => (
                <>{formatInteger(sumSubscriptionPeriodsSharesSubscribed(params.row.subscriptionPeriods), { locale })}</>
            ),
        },
        {
            field: 'subscribedAmount',
            headerName: 'Tecknat belopp',
            flex: 1,
            valueGetter: (_, row) => calculateTotalAmount(row.subscriptionPeriods),
            sortComparator: (v1, v2) => {
                return v1 - v2
            },
            renderCell: (params) => {
                return calculateTotalAmountAndFormat(locale, params.row)
            },
        },
        {
            field: 'unlisted',
            headerName: 'Onoterad',
            flex: 0.5,
            valueFormatter: (value) => (value ? 'Ja' : ''),
        },
        {
            field: 'outcome',
            headerName: 'Utfall',
            headerAlign: 'center',
            align: 'center',
            minWidth: 200,
            flex: 1,
            renderCell: (params) => {
                return <WarrantOutcomeChart warrant={params.row} />
            },
        },
    ]
    const columns = fields ? cols.filter((c) => fields.includes(c.field)) : cols
    if (entityIdsWithNotes) {
        columns.push(createNoteIndicatorColumn(entityIdsWithNotes, 'warrant', reloadEntityNoteIds))
    }
    if (!warrants || warrants.length === 0) {
        return <NoWarrants />
    }
    const disableSelectedSellOutlineStyle = {
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: 'none',
        },
    }

    return (
        // adapt filter: https://mui.com/x/react-data-grid/filtering/customization/#create-a-custom-operator
        <DataGridPro
            sx={{
                ...disableSelectedSellOutlineStyle,
                width: '100%',
                '& .even': {
                    backgroundColor: theme.palette.rowColor.main, // Use palette color for even rows
                },
                '& .odd': {
                    // backgroundColor: theme.palette.rowColor.light, // Use palette color for odd rows
                },
                '& .MuiDataGrid-cell': {
                    paddingY: 4,
                },
                ...noteIndicatorGridSx,
            }}
            localeText={gridTextI18n(locale)}
            pagination={pagination}
            initialState={{
                sorting: { sortModel },
            }}
            pageSizeOptions={pageSizeOptions}
            disableColumnMenu
            disableRowSelectionOnClick
            rows={warrants}
            autoHeight
            getRowHeight={() => 'auto'}
            columns={columns}
            density='standard'
            getRowId={(row) => `${row.id}:${row.issueId}`}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
            slots={{ noResultsOverlay: CustomNoResultsOverlay, toolbar: CustomDataGridToolbar }}
            paginationModel={{ pageSize, page: warrantGridCurrentPage }}
            onPaginationModelChange={({ page, pageSize }) => {
                setWarrantGridCurrentPage(page)
                setPageSize(pageSize)
            }}
            filterModel={filterModel}
            // Capture changes to the filter model
            onFilterModelChange={handleFilterModelChange}
            showToolbar
        />
    )
}

export default WarrantsGrid
